import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumbs from '../navigation/Breadcrumbs';
import { buildServiceBreadcrumbs } from '../navigation/breadcrumbUtils';
import { getBreadcrumbSchema } from '../navigation/breadcrumbSchema';
import SEO from '../seo/SEO';
import './HRServicesPage.css';

const AnimatedHeading = ({ as: Tag, className, children }) => {
  const text = typeof children === 'string' ? children : '';

  return (
    <Tag className={className} aria-label={text}>
      <span className="hr-heading-text" aria-hidden="true">
        {text.split('').map((char, index) => (
          <span
            key={`${text}-${index}`}
            className={`hr-heading-letter${
              char === ' ' ? ' hr-heading-letter-space' : ''
            }`}
            style={{ '--letter-index': index }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </Tag>
  );
};

const HRServices = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const servicesIndexView = location.state?.servicesIndexView;
  const backPath =
    servicesIndexView === 'hr' ||
    location.pathname.startsWith('/services/hr/')
      ? '/services/hr'
      : servicesIndexView
        ? '/services'
        : '/';

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsVisible(true);
    }, 80);

    return () => window.clearTimeout(timer);
  }, []);

  const breadcrumbItems = buildServiceBreadcrumbs('hr', 'HR Services');
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="standard-page-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link
            to={backPath}
            state={servicesIndexView ? { servicesIndexView } : undefined}
          >
            {servicesIndexView ? '\u2190 Back' : '\u2190 Back to Home'}
          </Link>
        </div>

        <div className="standard-page-hero">
          <AnimatedHeading
            as="h1"
            className={`hr-main-heading hr-heading-hero${
              isVisible ? ' is-visible' : ''
            }`}
          >
            HR Services
          </AnimatedHeading>
          <p>
            BlackRoth delivers comprehensive HR Services that help
            organizations manage their workforce efficiently, ensure
            compliance, and build scalable people operations aligned
            with business growth.
          </p>
        </div>

        <section className="standard-page-section">
          <h2>Overview</h2>
          <p>
            Human Resources is a strategic function that impacts productivity,
            compliance, and employee satisfaction. BlackRoth provides
            end-to-end HR services that streamline workforce management,
            reduce administrative overhead, and support organizational goals.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Core Capabilities</h2>
          <p>
            Our HR solutions are designed to manage the complete employee
            lifecycle while ensuring accuracy, compliance, and operational
            efficiency across diverse business environments.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>HR Services We Offer</h2>
          <ul className="standard-service-list">
            <li>HR Operations & Workforce Management</li>
            <li>Employee Onboarding & Offboarding</li>
            <li>Attendance & Leave Management</li>
            <li>HR Policy Design & Documentation</li>
            <li>Performance Management Support</li>
            <li>Employee Records & Data Management</li>
            <li>Compliance & Labor Law Support</li>
            <li>HR Process Automation & Optimization</li>
          </ul>
        </section>

        <section className="standard-page-section">
          <h2>Business Value</h2>
          <p>
            BlackRoth's HR Services help organizations improve workforce
            efficiency, ensure regulatory compliance, enhance employee
            engagement, and enable HR teams to focus on strategic initiatives
            rather than manual processes.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Technology & Integration</h2>
          <p>
            Our HR services integrate seamlessly with HRMS platforms,
            payroll systems, attendance tools, and enterprise software
            using secure, cloud-based and API-driven architectures.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Security & Compliance</h2>
          <p>
            BlackRoth ensures strict confidentiality and data protection
            through role-based access controls, encryption, audit trails,
            and adherence to employment and data protection regulations.
          </p>
        </section>
      </div>
    </div>
  );
};

export default HRServices;
